import type {
  DatabaseAdapter,
  LoggerAdapter,
  PairingController,
  PairingState,
  PlaybackPlan,
  PlayerApiClient,
  PlayerRuntime,
  RuntimeStatus,
} from '@signage/player-core';

/**
 * `window.signage` for the players whose runtime shares the page with the playback UI: Android
 * (a WebView) and the web player (a browser).
 *
 * The Electron player reaches this same surface over IPC from a sandboxed preload script. Here
 * the playback UI and the runtime share one JavaScript context, so the bridge is direct calls
 * rather than messages — but the shape is identical, which is what lets `packages/player-ui` be
 * used by all of them without a single conditional.
 */

type Handler<T> = (value: T) => void;

interface BridgeConfig {
  logger: LoggerAdapter;
  /** Maps a cached file's hash to a URL the WebView is allowed to load. */
  mediaUrl: (sha256: string) => string;
  /**
   * The same mapping expressed as a prefix, for the playback surface.
   *
   * The surface resolves a URL for every frame it shows and cannot await the bridge to do it,
   * so it is handed the prefix once at startup and appends each hash itself.
   */
  mediaUrlPrefix: string;
  /**
   * Choosing the device's home app (launcher). Only Android supplies it; the playback surface
   * shows its "make this the home screen" button only when it is present.
   */
  homeScreen?: HomeScreenControl;
}

export interface HomeScreenControl {
  status(): Promise<{ isHome: boolean; canChoose: boolean; overlayAllowed: boolean }>;
  choose(): Promise<{ opened: boolean }>;
}

export interface PlayerBridge {
  /** Supplies the dependencies that are only available once the async bootstrap has run. */
  configure(config: BridgeConfig): void;
  attach(runtime: PlayerRuntime, pairing: PairingController, database: DatabaseAdapter): void;
  emitPlan(plan: PlaybackPlan): void;
  emitStatus(status: RuntimeStatus): void;
  emitPairing(state: PairingState): void;
  emitIdentify(durationMs: number): void;
  captureScreenshot(api: PlayerApiClient): Promise<boolean>;
  /** Milliseconds since the playback surface last reported itself alive. */
  lastHeartbeatAge(): number;
}

/**
 * Installs `window.signage` immediately.
 *
 * This must run synchronously, before the playback surface's script does, because the surface
 * subscribes the moment it loads. The Electron player gets that ordering for free from its
 * preload script; here it is bought by installing the surface first and filling in the runtime
 * behind it, which is why `configure` and `attach` are separate from construction.
 */
export function installBridge(): PlayerBridge {
  const planHandlers = new Set<Handler<unknown>>();
  const statusHandlers = new Set<Handler<unknown>>();
  const pairingHandlers = new Set<Handler<unknown>>();
  const identifyHandlers = new Set<Handler<{ durationMs: number }>>();

  let runtime: PlayerRuntime | null = null;
  let pairing: PairingController | null = null;
  let config: BridgeConfig | null = null;
  let lastHeartbeat = Date.now();

  // Until the bootstrap finishes, log to the console: a failure during startup is exactly when
  // the log matters most, and the file logger does not exist yet.
  const log = (): LoggerAdapter =>
    config?.logger ?? {
      debug: (m, c) => console.debug(m, c ?? ''),
      info: (m, c) => console.info(m, c ?? ''),
      warn: (m, c) => console.warn(m, c ?? ''),
      error: (m, c) => console.error(m, c ?? ''),
    };

  const subscribe = <T>(set: Set<Handler<T>>, handler: Handler<T>): (() => void) => {
    set.add(handler);
    return () => set.delete(handler);
  };

  const emit = <T>(set: Set<Handler<T>>, value: T): void => {
    for (const handler of set) {
      try {
        handler(value);
      } catch (err) {
        // One misbehaving listener must not stop the others, or stop playback.
        log().error('Bridge listener threw', { error: (err as Error).message });
      }
    }
  };

  const api = {
    homeScreen: {
      // Present on every bridge so the surface can ask; null means this player can't choose.
      status: async () => (config?.homeScreen ? config.homeScreen.status() : null),
      choose: async () => (config?.homeScreen ? config.homeScreen.choose() : { opened: false }),
    },
    getState: async () => ({
      plan: runtime?.currentPlan ?? null,
      status: runtime?.currentStatus ?? null,
      pairing: pairing?.state ?? null,
      settings: runtime?.playerSettings ?? null,
      mediaUrlPrefix: config?.mediaUrlPrefix ?? '',
    }),

    mediaUrl: async (sha256: string) => config?.mediaUrl(sha256) ?? '',

    /** The file's address on the server, for a screen that cannot play the downloaded copy. */
    remoteMediaUrl: async (sha256: string) => runtime?.getManifest()?.media.find((m) => m.sha256 === sha256)?.url ?? null,

    requestSync: async () => {
      await runtime?.checkForUpdates('manual');
      return runtime?.currentStatus ?? null;
    },

    refreshPlan: async () => runtime?.refreshPlan('renderer-request') ?? null,

    reportNowPlaying: (info: {
      playlistId: string | null;
      mediaId: string | null;
      scheduleId: string | null;
    }) => {
      runtime?.setCurrentPlayback({
        playlistId: info?.playlistId ?? null,
        mediaId: info?.mediaId ?? null,
        scheduleId: info?.scheduleId ?? null,
      });
    },

    reportProofOfPlay: (entry: unknown) => {
      runtime?.recordProofOfPlay(entry as Parameters<PlayerRuntime['recordProofOfPlay']>[0]);
    },

    reportEvent: (entry: unknown) => {
      runtime?.recordEvent(entry as Parameters<PlayerRuntime['recordEvent']>[0]);
    },

    heartbeat: () => {
      lastHeartbeat = Date.now();
    },

    onPlan: (handler: Handler<unknown>) => subscribe(planHandlers, handler),
    onStatus: (handler: Handler<unknown>) => subscribe(statusHandlers, handler),
    onPairing: (handler: Handler<unknown>) => subscribe(pairingHandlers, handler),
    onIdentify: (handler: Handler<{ durationMs: number }>) =>
      subscribe(identifyHandlers, handler),
  };

  (window as unknown as { signage: typeof api }).signage = api;

  return {
    configure(next) {
      config = next;
    },
    attach(nextRuntime, nextPairing) {
      runtime = nextRuntime;
      pairing = nextPairing;
    },
    emitPlan: (plan) => emit(planHandlers, plan),
    emitStatus: (status) => emit(statusHandlers, status),
    emitPairing: (state) => emit(pairingHandlers, state),
    emitIdentify: (durationMs) => emit(identifyHandlers, { durationMs }),
    lastHeartbeatAge: () => Date.now() - lastHeartbeat,

    /**
     * Captures what is actually on the screen and uploads it.
     *
     * The WebView cannot screenshot itself the way Electron can, and Android's MediaProjection
     * needs a permission prompt that nobody is present to accept on a signage device. Instead
     * the page is redrawn into a canvas from the elements it is already showing, which is enough
     * to answer the question the dashboard is asking — is this screen displaying the right
     * thing, or is it blank.
     */
    async captureScreenshot(client: PlayerApiClient): Promise<boolean> {
      try {
        const blob = await renderPageToJpeg();
        if (!blob) return false;
        const bytes = new Uint8Array(await blob.arrayBuffer());
        await client.uploadScreenshot(bytes, { width: window.innerWidth, height: window.innerHeight });
        return true;
      } catch (err) {
        log().error('Screenshot failed', { error: (err as Error).message });
        return false;
      }
    },
  };
}

/**
 * Draws the current media element onto a canvas and encodes it as JPEG.
 *
 * Only the media matters for proof that a screen is alive, so this deliberately captures the
 * image or video frame rather than trying to rasterise the whole DOM, which a WebView cannot do
 * without tainting the canvas.
 */
async function renderPageToJpeg(): Promise<Blob | null> {
  const canvas = document.createElement('canvas');
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // The layer actually on screen: the player swaps between two, each holding a picture, a video
  // and a menu area, and only the visible one's showing element is what people see.
  const layer = document.querySelector<HTMLElement>('#stage .layer.visible');
  const shown = (el: HTMLElement | null) => Boolean(el && el.style.display !== 'none');
  const image = layer?.querySelector<HTMLImageElement>('img.media') ?? null;
  const video = layer?.querySelector<HTMLVideoElement>('video.media') ?? null;
  const menu = layer?.querySelector<HTMLElement>('.menu-surface') ?? null;
  const media =
    shown(video) && video!.videoWidth > 0 ? video : shown(image) && image!.naturalWidth > 0 ? image : null;
  const panel = document.querySelector<HTMLElement>('.fullscreen-panel:not(.hidden)');

  const drawText = (lines: string[]) => {
    ctx.fillStyle = '#e2e8f0';
    ctx.font = `${Math.round(canvas.height / 20)}px sans-serif`;
    ctx.textAlign = 'center';
    lines.slice(0, 2).forEach((line, i) => {
      ctx.fillText(line, canvas.width / 2, canvas.height / 2 + i * canvas.height / 14);
    });
  };

  if (panel) {
    // Pairing or the logo screen covers the stage: capture that, so the dashboard shows why.
    drawText((panel.innerText || 'No content').split(/\r?\n/).map((l) => l.trim()).filter(Boolean));
  } else if (media) {
    const natural =
      media instanceof HTMLVideoElement
        ? { w: media.videoWidth, h: media.videoHeight }
        : { w: media.naturalWidth, h: media.naturalHeight };
    // Match the letterboxing the player itself applies, so the capture looks like the screen.
    const scale = Math.min(canvas.width / natural.w, canvas.height / natural.h);
    const w = natural.w * scale;
    const h = natural.h * scale;
    ctx.drawImage(media, (canvas.width - w) / 2, (canvas.height - h) / 2, w, h);
  } else if (shown(menu) && menu!.childElementCount > 0) {
    // A menu board is drawn as a web page, which a WebView cannot copy into an image; name it.
    const title = menu!.querySelector('h1, h2, h3')?.textContent?.trim();
    drawText(['Menu board', title || '']);
  } else {
    drawText(['No content']);
  }

  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.8));
}
